import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'

import {
  ProviderSelfHarmTaskArguments,
  ProviderTask,
  ProviderTriageRiskTaskArguments,
} from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import { selfHarmTasksStates } from '../../../../../common/constants/appConstants'
import { getAuthConfig, getAuthUserId } from '../../../../../data/auth/authSelectors'
import {
  useGetProviderSelfHarmTasksQuery,
  useGetProviderTriageRiskTasksQuery,
} from '../../../../../providers/dashboard/data/providerTasksApi'

export type ClientRiskStatus = 'at_risk' | 'addressed_visible' | null

type AllTasks = ProviderTask<ProviderSelfHarmTaskArguments | ProviderTriageRiskTaskArguments>

export type ClientRiskEntry = {
  status: ClientRiskStatus
  task: AllTasks
}

const useClientRiskStatus = (): Map<string, ClientRiskEntry> => {
  const userId = useSelector(getAuthUserId)
  const hideRiskAlerts = useSelector(getAuthConfig)?.hideRiskAlerts
  const queryArgs = hideRiskAlerts ? skipToken : { providerId: userId }
  const { data: selfHarmTasks } = useGetProviderSelfHarmTasksQuery(queryArgs)
  const { data: triageRiskTasks } = useGetProviderTriageRiskTasksQuery(queryArgs)

  const allTasks = useMemo(() => {
    const tasks: AllTasks[] = []
    if (selfHarmTasks) tasks.push(...selfHarmTasks)
    if (triageRiskTasks) tasks.push(...triageRiskTasks)
    return tasks
  }, [selfHarmTasks, triageRiskTasks])

  return useMemo(() => {
    const entryMap = new Map<string, ClientRiskEntry>()
    if (hideRiskAlerts) return entryMap

    allTasks.forEach((task) => {
      const patientId = task.arguments?.patient_id
      if (!patientId || task.status === selfHarmTasksStates.INACTIVE) return

      const isActive = task.status !== selfHarmTasksStates.ACKNOWLEDGED

      if (isActive) {
        entryMap.set(patientId, { status: 'at_risk', task })
      } else if (!entryMap.has(patientId)) {
        entryMap.set(patientId, { status: 'addressed_visible', task })
      }
    })

    return entryMap
  }, [allTasks, hideRiskAlerts])
}

export default useClientRiskStatus
